import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { MESApiService } from "@dexteel/mesf-core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	ForwardedRef,
	forwardRef,
	ReactNode,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import {
	DrawingViewState,
	getDrawingViewState,
	saveDrawingViewState,
} from "@/shared/repositories/DrawingViewStateRepository";

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

type Props = {
	drawingPath: string | null;
	productId?: number | null;
	toolbarLeft?: ReactNode;
	onDrawingUpload?: (url: string) => void;
	onDrawingClear?: () => void;
	onError?: (message: string) => void;
	// Reports whether the current view differs from the saved one, so the host
	// modal can enable its Save buttons and persist the view on submit.
	onDirtyChange?: (dirty: boolean) => void;
};

export type DrawingViewerHandle = {
	// Persists the current view; resolves false when the save failed.
	saveView: () => Promise<boolean>;
};

const DEFAULT_ZOOM = 60;
const ZOOM_STORAGE_KEY = "drawing-zoom-";

function getStoredZoom(productId?: number | null): number {
	if (!productId) return DEFAULT_ZOOM;
	try {
		const stored = localStorage.getItem(ZOOM_STORAGE_KEY + productId);
		if (stored) {
			const val = Number(stored);
			if (!isNaN(val) && val >= 25 && val <= 300) return val;
		}
	} catch {}
	return DEFAULT_ZOOM;
}

function storeZoom(productId: number | null | undefined, zoom: number) {
	if (!productId) return;
	try {
		localStorage.setItem(ZOOM_STORAGE_KEY + productId, String(zoom));
	} catch {}
}

const DrawingViewerImpl = (
	{
		drawingPath,
		productId,
		toolbarLeft,
		onDrawingUpload,
		onDrawingClear,
		onError,
		onDirtyChange,
	}: Props,
	ref: ForwardedRef<DrawingViewerHandle>,
) => {
	const safePath =
		drawingPath && window.location.protocol === "https:"
			? drawingPath.replace(/^http:\/\//i, "https://")
			: drawingPath;
	const isPdf = safePath?.toLowerCase().endsWith(".pdf");

	const [zoom, setZoom] = useState(DEFAULT_ZOOM);
	const [isUploading, setIsUploading] = useState(false);
	const [isSavingView, setIsSavingView] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isPanning, setIsPanning] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
	const [numPages, setNumPages] = useState<number>(0);
	const panStartRef = useRef({ x: 0, y: 0 });
	const positionRef = useRef({ x: 0, y: 0 });
	const contentRef = useRef<HTMLDivElement | null>(null);

	const queryClient = useQueryClient();

	// Saved view (shared, per drawing) takes precedence over the local zoom
	const { data: savedView } = useQuery<DrawingViewState | null>({
		queryKey: ["drawingViewState", safePath],
		queryFn: () => getDrawingViewState(safePath as string),
		enabled: !!safePath,
	});

	// The view the current one is compared against to derive dirtiness: the
	// shared saved view when there is one, else the initial local view.
	const [baseline, setBaseline] = useState({ zoom: DEFAULT_ZOOM, x: 0, y: 0 });

	// Reset pan when drawing changes; restore zoom from localStorage per product
	useEffect(() => {
		const zoom = getStoredZoom(productId);
		setPosition({ x: 0, y: 0 });
		positionRef.current = { x: 0, y: 0 };
		setZoom(zoom);
		setBaseline({ zoom, x: 0, y: 0 });
		setNumPages(0);
	}, [safePath, productId]);

	useEffect(() => {
		if (!savedView) return;
		setZoom(savedView.Zoom);
		setPosition({ x: savedView.OffsetX, y: savedView.OffsetY });
		positionRef.current = { x: savedView.OffsetX, y: savedView.OffsetY };
		setBaseline({
			zoom: savedView.Zoom,
			x: savedView.OffsetX,
			y: savedView.OffsetY,
		});
	}, [savedView]);

	const isViewDirty =
		!!safePath &&
		(Math.round(zoom) !== Math.round(baseline.zoom) ||
			Math.round(position.x) !== Math.round(baseline.x) ||
			Math.round(position.y) !== Math.round(baseline.y));

	// Ref so the report effect always calls the latest callback without
	// re-firing when the host re-renders with a new inline lambda.
	const onDirtyChangeRef = useRef(onDirtyChange);
	useEffect(() => {
		onDirtyChangeRef.current = onDirtyChange;
	}, [onDirtyChange]);
	useEffect(() => {
		onDirtyChangeRef.current?.(isViewDirty);
	}, [isViewDirty]);

	const handleSaveView = async (): Promise<boolean> => {
		if (!safePath) return true;
		setIsSavingView(true);
		try {
			await saveDrawingViewState(safePath, zoom, position.x, position.y);
			queryClient.setQueryData(["drawingViewState", safePath], {
				DrawingPath: safePath,
				Zoom: Math.round(zoom),
				OffsetX: Math.round(position.x),
				OffsetY: Math.round(position.y),
			});
			return true;
		} catch (err) {
			const message =
				err instanceof Error ? err.message : "Failed to save drawing view";
			onError?.(message);
			return false;
		} finally {
			setIsSavingView(false);
		}
	};

	useImperativeHandle(ref, () => ({ saveView: handleSaveView }));

	// Mouse wheel zoom (registered as non-passive to allow preventDefault)
	useEffect(() => {
		const el = contentRef.current;
		if (!el) return;

		const handleWheel = (e: WheelEvent) => {
			if (!safePath) return;
			e.preventDefault();
			setZoom((prev) => {
				const delta = e.deltaY > 0 ? -5 : 5;
				const next = Math.min(Math.max(prev + delta, 25), 300);
				storeZoom(productId, next);
				return next;
			});
		};

		el.addEventListener("wheel", handleWheel, { passive: false });
		return () => el.removeEventListener("wheel", handleWheel);
	}, [safePath]);

	const handleZoomIn = () => {
		setZoom((prev) => {
			const next = Math.min(prev + 10, 300);
			storeZoom(productId, next);
			return next;
		});
	};

	const handleZoomOut = () => {
		setZoom((prev) => {
			const next = Math.max(prev - 10, 25);
			storeZoom(productId, next);
			return next;
		});
	};

	const handleResetView = () => {
		setZoom(DEFAULT_ZOOM);
		setPosition({ x: 0, y: 0 });
		positionRef.current = { x: 0, y: 0 };
	};

	const handleFullscreen = () => {
		if (safePath) {
			window.open(safePath, "_blank");
		}
	};

	// Pan handlers
	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => {
			if (!safePath) return;
			e.preventDefault();
			setIsPanning(true);
			panStartRef.current = { x: e.clientX, y: e.clientY };
			positionRef.current = position;
		},
		[safePath, position],
	);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => {
			if (!isPanning) return;
			const dx = e.clientX - panStartRef.current.x;
			const dy = e.clientY - panStartRef.current.y;
			setPosition({
				x: positionRef.current.x + dx,
				y: positionRef.current.y + dy,
			});
		},
		[isPanning],
	);

	const handleMouseUp = useCallback(() => {
		setIsPanning(false);
	}, []);

	// Upload handler
	const handleUpload = useCallback(
		async (acceptedFiles: File[]) => {
			if (!onDrawingUpload || acceptedFiles.length === 0) return;

			setIsUploading(true);
			try {
				const api = new MESApiService();
				const response = await api.uploadFiles(acceptedFiles, "drawings");
				const url = response?.result?.[0]?.URL;
				if (url) {
					onDrawingUpload(url);
				}
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Failed to upload drawing";
				onError?.(message);
			} finally {
				setIsUploading(false);
			}
		},
		[onDrawingUpload, onError],
	);

	const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
		onDrop: handleUpload,
		accept: { "image/*": [], "application/pdf": [".pdf"] },
		multiple: false,
		noClick: true,
		noKeyboard: true,
		disabled: !onDrawingUpload,
	});

	const isUploadEnabled = !!onDrawingUpload;
	const isEmpty = !safePath;

	// Merge dropzone ref with our contentRef
	const dropzoneProps = isUploadEnabled ? getRootProps() : {};
	const dropzoneRef = (dropzoneProps as any).ref;
	const mergedRef = useCallback(
		(node: HTMLDivElement | null) => {
			contentRef.current = node;
			if (typeof dropzoneRef === "function") {
				dropzoneRef(node);
			}
		},
		[dropzoneRef],
	);

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Toolbar */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
					p: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
				}}
			>
				{toolbarLeft ?? <Box />}
				<Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
					{isUploadEnabled && (
						<IconButton
							size="small"
							color="primary"
							onClick={safePath ? () => setShowReplaceConfirm(true) : open}
							disabled={isUploading}
							title="Upload drawing"
						>
							{isUploading ? (
								<CircularProgress size={18} />
							) : (
								<CloudUploadIcon fontSize="small" />
							)}
						</IconButton>
					)}
					{isUploadEnabled && safePath && (
						<IconButton
							size="small"
							color="secondary"
							onClick={() => setShowDeleteConfirm(true)}
							disabled={isUploading}
							title="Remove drawing"
						>
							<DeleteIcon fontSize="small" />
						</IconButton>
					)}
					<IconButton size="small" onClick={handleZoomOut} disabled={!safePath}>
						<RemoveIcon fontSize="small" />
					</IconButton>
					<Typography
						variant="caption"
						sx={{
							mx: 1,
							minWidth: 40,
							display: "inline-block",
							textAlign: "center",
						}}
					>
						{zoom}%
					</Typography>
					<IconButton size="small" onClick={handleZoomIn} disabled={!safePath}>
						<AddIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						onClick={handleFullscreen}
						disabled={!safePath}
					>
						<FullscreenIcon fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						color="primary"
						onClick={handleSaveView}
						disabled={!safePath || isSavingView}
						title="Save current view as the default for this drawing"
					>
						{isSavingView ? (
							<CircularProgress size={18} />
						) : (
							<SaveIcon fontSize="small" />
						)}
					</IconButton>
				</Box>
			</Box>

			{/* Drawing Content */}
			<Box
				{...dropzoneProps}
				ref={mergedRef}
				onMouseDown={safePath ? handleMouseDown : undefined}
				onMouseMove={safePath ? handleMouseMove : undefined}
				onMouseUp={safePath ? handleMouseUp : undefined}
				onMouseLeave={safePath ? handleMouseUp : undefined}
				onClick={isEmpty && isUploadEnabled ? open : undefined}
				sx={{
					flex: 1,
					overflow: "hidden",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					bgcolor: "grey.100",
					position: "relative",
					cursor: safePath
						? isPanning
							? "grabbing"
							: "grab"
						: isUploadEnabled
							? "pointer"
							: "default",
					userSelect: "none",
					...(isEmpty &&
						isUploadEnabled && {
							border: "2px dashed",
							borderColor: isDragActive ? "primary.main" : "grey.400",
							borderRadius: 2,
							bgcolor: isDragActive ? "primary.50" : "grey.50",
							m: 1,
							transition: "border-color 0.2s, background-color 0.2s",
							"&:hover": {
								borderColor: "primary.main",
								bgcolor: "primary.50",
							},
						}),
				}}
			>
				{isUploadEnabled && <input {...getInputProps()} />}

				{isDragActive && !isEmpty && (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							bgcolor: "primary.50",
							border: "2px dashed",
							borderColor: "primary.main",
							borderRadius: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 10,
						}}
					>
						<Typography variant="body1" color="primary">
							Drop file to replace
						</Typography>
					</Box>
				)}

				{isUploading ? (
					<CircularProgress />
				) : safePath ? (
					<>
						<Box
							sx={{
								transform: `translate(${position.x}px, ${position.y}px) scale(${zoom / 100})`,
								transformOrigin: "center center",
								transition: isPanning ? "none" : "transform 0.2s ease",
							}}
						>
							{isPdf ? (
								<Document
									file={safePath}
									onLoadSuccess={({ numPages: n }) => setNumPages(n)}
									onLoadError={(err) =>
										onError?.(`Failed to load PDF: ${err.message}`)
									}
									loading={<CircularProgress />}
								>
									{Array.from({ length: numPages }, (_, i) => (
										<Page
											key={i + 1}
											pageNumber={i + 1}
											renderTextLayer={false}
											renderAnnotationLayer={false}
											scale={2}
										/>
									))}
								</Document>
							) : (
								<img
									src={safePath}
									alt="Product Drawing"
									draggable={false}
									style={{
										maxWidth: "100%",
										maxHeight: "100%",
										objectFit: "contain",
										userSelect: "none",
										pointerEvents: "none",
									}}
									onError={(e) => {
										(e.target as HTMLImageElement).style.display = "none";
									}}
								/>
							)}
						</Box>
						{(zoom !== DEFAULT_ZOOM ||
							position.x !== 0 ||
							position.y !== 0) && (
							<Button
								size="small"
								variant="contained"
								onClick={handleResetView}
								sx={{
									position: "absolute",
									bottom: 16,
									right: 16,
									zIndex: 10,
									textTransform: "none",
									opacity: 0.9,
								}}
							>
								Reset View
							</Button>
						)}
					</>
				) : isUploadEnabled ? (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 1.5,
						}}
					>
						<CloudUploadIcon
							sx={{
								fontSize: 48,
								color: isDragActive ? "primary.main" : "grey.400",
							}}
						/>
						<Typography
							variant="body1"
							color={isDragActive ? "primary" : "text.secondary"}
						>
							{isDragActive
								? "Drop file here"
								: "Drag & drop an image or PDF here"}
						</Typography>
						{!isDragActive && (
							<Typography variant="body2" color="text.disabled">
								or click to browse
							</Typography>
						)}
					</Box>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							gap: 1.5,
						}}
					>
						<Typography variant="body1" color="text.secondary">
							No drawing available
						</Typography>
					</Box>
				)}
			</Box>

			<ConfirmationDialogRaw
				title="Replace Drawing"
				message="A drawing already exists. Do you want to replace it with a new one?"
				open={showReplaceConfirm}
				onClose={(value) => {
					setShowReplaceConfirm(false);
					if (value === "OK") {
						open();
					}
				}}
			/>
			<ConfirmationDialogRaw
				title="Delete Drawing"
				message="Are you sure you want to delete this drawing? This action is permanent and cannot be undone."
				open={showDeleteConfirm}
				onClose={(value) => {
					setShowDeleteConfirm(false);
					if (value === "OK") {
						onDrawingClear?.();
					}
				}}
			/>
		</Box>
	);
};

export const DrawingViewer = forwardRef(DrawingViewerImpl);
